import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { useReviewParams } from './useReviewParams';

const log = createLogger('useReviewClassSummaryQuery');

export const QUERY_KEY = 'reviewClassSummary';

export type ReviewClassSummaryResponse = {
  markdown: string;
};

export function useReviewClassSummary({
  date,
  periodId,
  enabled = false,
}: {
  date: Date;
  periodId: string;
  enabled?: boolean;
}) {
  const httpClient = useHttpClient();
  const params = useReviewParams({ date, periodId });

  const paramsQueryStr = params.toQueryString();

  const { data, error, status, refetch } = useQuery<null | ReviewClassSummaryResponse>({
    queryKey: [QUERY_KEY, paramsQueryStr],
    queryFn: async () => {
      log.debug('Fetching review class summary', {
        day: params.day,
        periodId: params.periodId,
        timeZone: params.timeZone ?? 'unknown',
      });
      const { markdown } = await httpClient.get<ReviewClassSummaryResponse>(
        `/review/getClassSummary?${paramsQueryStr}`,
      );
      log.info('Fetched review class summary', {
        summaryBodyLength: markdown.length,
      });
      return { date, markdown };
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch review class summary', failureCount, error, log }),
    enabled: httpClient.isAuthenticated && enabled,
  });

  return { data, error, status, refetch };
}
